import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, ArrowRight, Leaf, FlaskConical, Clock } from "lucide-react";
import { api, resolveImage, formatDate } from "@/lib/api";
import { useLang } from "@/i18n";
import { usePageTitle } from "@/lib/useTitle";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/Footer";
import { TagPill } from "@/lib/colors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const RECENT_KEY = "pb_recent_searches";

function loadRecent() {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

function saveRecent(term) {
  const cur = loadRecent().filter((x) => x.toLowerCase() !== term.toLowerCase());
  const next = [term, ...cur].slice(0, 6);
  localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  return next;
}

function BatchCard({ batch, index, verifyLabel }) {
  return (
    <Link
      to={`/batch/${batch.batch_code}`}
      data-testid={`batch-card-${batch.batch_code}`}
      className="group block bg-white border border-border rounded-sm overflow-hidden transition-transform duration-200 hover:-translate-y-1 hover:shadow-[0_12px_30px_-12px_rgba(28,28,28,0.18)] animate-fade-up"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="aspect-[4/3] overflow-hidden bg-secondary">
        {batch.image_url ? (
          <img src={resolveImage(batch.image_url)} alt={batch.product_name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-gray-300">
            <Leaf strokeWidth={2} className="h-10 w-10" />
          </div>
        )}
      </div>
      <div className="p-5">
        <span className="font-data text-xs uppercase tracking-[0.15em] text-sage-dark font-semibold">{batch.batch_code}</span>
        <h3 className="font-heading text-xl mt-1 text-ink leading-tight">{batch.product_name}</h3>
        {batch.inherited_tags && batch.inherited_tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`card-inherited-tags-${batch.batch_code}`}>
            {batch.inherited_tags.map((tag) => (
              <TagPill key={tag.id} tag={tag} testid={`card-tag-${batch.batch_code}-${tag.id}`} />
            ))}
          </div>
        )}
        <div className="mt-4 flex items-center justify-between">
          <span className="font-data text-xs text-gray-400">{formatDate(batch.production_date)}</span>
          <span className="flex items-center gap-1 text-sm text-ink font-semibold opacity-0 -translate-x-2 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0">
            {verifyLabel} <ArrowRight className="h-4 w-4" />
          </span>
        </div>
      </div>
    </Link>
  );
}

function ProductsTab() {
  const { t } = useLang();
  const [batches, setBatches] = useState([]);
  const [tags, setTags] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [term, setTerm] = useState("");
  const [active, setActive] = useState("");
  const [recent, setRecent] = useState(loadRecent());
  const [sort, setSort] = useState("newest");
  const [tagFilter, setTagFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("all");

  useEffect(() => {
    api.get("/ingredient-tags").then((r) => setTags(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/batches", { params: active ? { q: active } : {} }).then((res) => setBatches(res.data)).finally(() => setLoading(false));
  }, [active]);

  const runSearch = (value) => {
    const v = value.trim();
    setActive(v);
    if (v) setRecent(saveRecent(v));
  };

  const submit = (e) => {
    e.preventDefault();
    runSearch(term);
  };

  const filtersActive = tagFilter !== "all" || catFilter !== "all";
  const resetFilters = () => { setTagFilter("all"); setCatFilter("all"); };

  const displayed = [...batches]
    .filter((b) => catFilter === "all" || (b.ingredients || []).some((i) => i.category === catFilter))
    .filter((b) => tagFilter === "all" || (b.ingredients || []).some((i) => (i.tags || []).some((tg) => tg.id === tagFilter)))
    .sort((a, b) => {
      const da = a.production_date || "";
      const db = b.production_date || "";
      return sort === "newest" ? db.localeCompare(da) : da.localeCompare(db);
    });

  return (
    <div>
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 max-w-2xl mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" strokeWidth={2} />
          <Input
            data-testid="batch-search-input"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder={t("archive.search.placeholder")}
            className="h-12 py-3 pl-12 rounded-sm border-border font-data text-base focus-visible:ring-sage focus-visible:ring-2 focus-visible:border-sage"
          />
        </div>
        <Button type="submit" data-testid="batch-search-button" className="h-12 px-8 rounded-sm bg-ink text-white hover:bg-sage-dark font-semibold">
          {t("archive.search.button")}
        </Button>
        {active && (
          <Button type="button" variant="ghost" onClick={() => { setTerm(""); setActive(""); }} className="h-12 rounded-sm text-gray-500 font-semibold">
            {t("archive.search.clear")}
          </Button>
        )}
      </form>

      {recent.length > 0 && (
        <div className="flex items-center flex-wrap gap-2 mb-6" data-testid="recent-searches">
          <span className="inline-flex items-center gap-1.5 font-data text-xs uppercase tracking-[0.12em] text-gray-400">
            <Clock className="h-3.5 w-3.5" /> {t("archive.recent")}
          </span>
          {recent.map((r) => (
            <button
              key={r}
              data-testid={`recent-chip-${r}`}
              onClick={() => { setTerm(r); runSearch(r); }}
              className="px-3 py-1 rounded-full border border-border bg-white text-sm font-data text-ink hover:border-sage hover:bg-sage/10 transition-colors duration-200"
            >
              {r}
            </button>
          ))}
        </div>
      )}

      {/* Sort & filter controls */}
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-10" data-testid="archive-controls">
        <div className="flex flex-col gap-1.5">
          <span className="font-data text-xs uppercase tracking-[0.12em] text-gray-500 font-semibold">{t("archive.sort.label")}</span>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="archive-sort-select" className="h-11 w-full sm:w-56 rounded-sm font-data text-sm focus:ring-sage"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest" data-testid="sort-newest">{t("archive.sort.newest")}</SelectItem>
              <SelectItem value="oldest" data-testid="sort-oldest">{t("archive.sort.oldest")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="font-data text-xs uppercase tracking-[0.12em] text-gray-500 font-semibold">{t("archive.filter.category")}</span>
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger data-testid="archive-category-filter" className="h-11 w-full sm:w-48 rounded-sm font-data text-sm focus:ring-sage"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="category-filter-all">{t("archive.filter.all")}</SelectItem>
              {categories.map((c) => (<SelectItem key={c} value={c} data-testid={`category-filter-${c}`}>{c}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="font-data text-xs uppercase tracking-[0.12em] text-gray-500 font-semibold">{t("archive.filter.tag")}</span>
          <Select value={tagFilter} onValueChange={setTagFilter}>
            <SelectTrigger data-testid="archive-tag-filter" className="h-11 w-full sm:w-48 rounded-sm font-data text-sm focus:ring-sage"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="tag-filter-all">{t("archive.filter.all")}</SelectItem>
              {tags.map((tg) => (<SelectItem key={tg.id} value={tg.id} data-testid={`tag-filter-${tg.id}`}>{tg.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        {filtersActive && (
          <Button type="button" variant="ghost" onClick={resetFilters} data-testid="archive-reset-filters" className="h-11 rounded-sm text-gray-500 font-semibold">
            {t("archive.filter.reset")}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[...Array(3)].map((_, i) => (<div key={i} className="aspect-[4/3] rounded-sm bg-secondary animate-pulse" />))}
        </div>
      ) : displayed.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-border rounded-sm" data-testid="empty-state">
          <Leaf className="h-10 w-10 mx-auto text-gray-300" strokeWidth={2} />
          <p className="mt-4 font-heading text-2xl text-ink">{t("archive.empty.title")}</p>
          <p className="mt-1 text-gray-500 text-sm">{active || filtersActive ? t("archive.empty.search") : t("archive.empty.default")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayed.map((b, i) => (<BatchCard key={b.id} batch={b} index={i} verifyLabel={t("common.verify")} />))}
        </div>
      )}
    </div>
  );
}

function IngredientsTab() {
  const { t } = useLang();
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/ingredients").then((res) => setIngredients(res.data)).finally(() => setLoading(false));
  }, []);

  const grouped = ingredients.reduce((acc, ing) => {
    (acc[ing.category] = acc[ing.category] || []).push(ing);
    return acc;
  }, {});

  if (loading) {
    return (<div className="space-y-3">{[...Array(4)].map((_, i) => (<div key={i} className="h-14 rounded-sm bg-secondary animate-pulse" />))}</div>);
  }

  if (ingredients.length === 0) {
    return (
      <div className="text-center py-24 border border-dashed border-border rounded-sm">
        <FlaskConical className="h-10 w-10 mx-auto text-gray-300" strokeWidth={2} />
        <p className="mt-4 font-heading text-2xl text-ink">{t("archive.ing.empty")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-12" data-testid="ingredients-list">
      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat}>
          <div className="flex items-baseline gap-3 mb-4">
            <h3 className="font-heading text-2xl text-ink">{cat}</h3>
            <span className="font-data text-xs text-gray-400">{items.length}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((ing) => (
              <Link
                key={ing.id}
                to={`/ingredient/${ing.id}`}
                data-testid={`ingredient-link-${ing.id}`}
                className="group flex items-center justify-between p-4 bg-white border border-border rounded-sm hover:border-sage transition-colors duration-200"
              >
                <div className="min-w-0">
                  <p className="font-data text-sm text-ink truncate">{ing.name}</p>
                  {ing.tags && ing.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {ing.tags.map((tg) => (<TagPill key={tg.id} tag={tg} />))}
                    </div>
                  )}
                </div>
                <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-sage-dark transition-colors duration-200 shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Archive() {
  const { t } = useLang();
  usePageTitle(t("title.archive"));
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "suroviny" ? "suroviny" : "produkty";

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <span className="font-data text-xs uppercase tracking-[0.2em] text-gray-500 font-semibold">{t("archive.kicker")}</span>
        <h1 className="font-heading text-4xl sm:text-5xl text-ink mt-1 mb-8">{t("archive.title")}</h1>

        <Tabs defaultValue={initialTab} onValueChange={(v) => setSearchParams(v === "suroviny" ? { tab: "suroviny" } : {})}>
          <TabsList className="rounded-sm bg-secondary mb-10">
            <TabsTrigger value="produkty" data-testid="tab-products" className="rounded-sm font-semibold text-sm data-[state=active]:bg-white">
              {t("archive.tab.products")}
            </TabsTrigger>
            <TabsTrigger value="suroviny" data-testid="tab-ingredients" className="rounded-sm font-semibold text-sm data-[state=active]:bg-white">
              {t("archive.tab.ingredients")}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="produkty"><ProductsTab /></TabsContent>
          <TabsContent value="suroviny"><IngredientsTab /></TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
}
